import {
  createGetAPI,
  createGetAllAPI,
  createPostAPI,
  createPatchAPI,
} from '@cuemath/rest-api';
import { stringify } from 'query-string';
import { BASE_URL } from 'constants/api';

const CHATBOT_URL = `${BASE_URL}/chatbot`;

// --- Agent Profile ---

const { useGet: useGetAgentDetails, invalidate: invalidateAgentDetails } = createGetAPI({
  getURL: id => `${CHATBOT_URL}/agents/${id}/`,
});

const { useGet: useGetAgents, invalidate: invalidateAgents } = createGetAPI({
  getURL: () => `${CHATBOT_URL}/agents/`,
});

const { usePatch: usePatchAgentDetails } = createPatchAPI({
  getURL: id => `${CHATBOT_URL}/agents/${id}/`,
});

const { usePost: usePostAgent } = createPostAPI({
  getURL: () => `${CHATBOT_URL}/agents/`,
});

// --- Tickets ---

const { useGetAll: useGetTickets, invalidate: invalidateTickets } = createGetAllAPI({
  getURL: query => `${CHATBOT_URL}/tickets/?${stringify(query)}`,
});

const { useGet: useGetTicketDetail, invalidate: invalidateTicketDetail } = createGetAPI({
  getURL: (ticketId, query) =>
    `${CHATBOT_URL}/tickets/${ticketId}/?${stringify({
      caller_type: 'agent',
      ...query,
    })}`,
});

const { usePatch: usePatchTicket } = createPatchAPI({
  getURL: ticketId => `${CHATBOT_URL}/tickets/${ticketId}/`,
});

// --- Messages ---

const { useGetAll: useGetMessages, invalidate: invalidateMessages } = createGetAllAPI({
  getURL: (ticketId, query) =>
    `${CHATBOT_URL}/tickets/${ticketId}/messages/?${stringify(query || {})}`,
});

const { usePost: usePostMessage } = createPostAPI({
  getURL: (_, ticketId) => `${CHATBOT_URL}/tickets/${ticketId}/messages/`,
});

// --- Saved Filters ---

const { useGet: useGetFilters, invalidate: invalidateFilters } = createGetAPI({
  getURL: () => `${CHATBOT_URL}/filters/`,
});

const { usePost: usePostFilter } = createPostAPI({
  getURL: () => `${CHATBOT_URL}/filters/`,
});

const { usePatch: usePatchFilter } = createPatchAPI({
  getURL: filterId => `${CHATBOT_URL}/filters/${filterId}/`,
});

// --- Category Tree ---

const { useGet: useGetCategoryTree } = createGetAPI({
  getURL: () => `${CHATBOT_URL}/dispositions/tree/`,
});

// --- Customer Search ---

const { useGet: useGetUsers } = createGetAPI({
  getURL: (_, query) => `${CHATBOT_URL}/users/?${stringify(query || {})}`,
});

// --- Escalation Teams (picker source) ---

const { useGet: useGetEscalationTeams } = createGetAPI({
  getURL: () => `${CHATBOT_URL}/escalation-teams/`,
});

// --- Support Teams (CRUD) ---

// createGetAPI keys the redux-rest cache on the FIRST arg; an object key
// breaks listener equality (a Proxy stringifies on set, getKey returns the
// raw object on read → '[object Object]' !== <obj>, listener never fires).
// Pass a plain string id like useGetAgents does. The id doubles as the
// `?include=` value: 'default' (or empty) → no include param.
const { useGet: useGetSupportTeams, invalidate: invalidateSupportTeams } = createGetAPI({
  getURL: id =>
    `${CHATBOT_URL}/support-teams/${
      id && id !== 'default' ? `?${stringify({ include: id })}` : ''
    }`,
});

const { usePost: usePostSupportTeam } = createPostAPI({
  getURL: () => `${CHATBOT_URL}/support-teams/`,
});

// PATCH handles both rename ({name}) and soft-delete ({is_deleted: true}).
const { usePatch: usePatchSupportTeam } = createPatchAPI({
  getURL: id => `${CHATBOT_URL}/support-teams/${id}/`,
});

export {
  // Agent Profile
  useGetAgentDetails,
  invalidateAgentDetails,
  useGetAgents,
  invalidateAgents,
  usePatchAgentDetails,
  usePostAgent,
  // Tickets
  useGetTickets,
  invalidateTickets,
  useGetTicketDetail,
  invalidateTicketDetail,
  usePatchTicket,
  // Messages
  useGetMessages,
  invalidateMessages,
  usePostMessage,
  // Filters
  useGetFilters,
  invalidateFilters,
  usePostFilter,
  usePatchFilter,
  // Category Tree
  useGetCategoryTree,
  // Customer Search
  useGetUsers,
  // Escalation Teams
  useGetEscalationTeams,
  // Support Teams
  useGetSupportTeams,
  invalidateSupportTeams,
  usePostSupportTeam,
  usePatchSupportTeam,
};
